import React, {useEffect, useMemo, useState} from "react";
import {ResponsiveRecipeFilter} from "../filter/ResponsiveRecipeFilter";
import Preloader from "../Preloader";
import {isMobile, MAX_RECIPES_FOR_SUBSCRIPTION, MIN_RECIPES_FOR_SUBSCRIPTION} from "../helper";
import {useSelectedTagHandles} from "../filter/useSelectedTagHandles";
import {useWeekRecipes} from "../v2/recipe/useWeekRecipes";
import {useMealkitCart} from "./useMealkitCart";
import {WeekDate} from "../v2/weekDate";
import RecipeCard from "../v2/recipe/RecipeCard";
import IntroDialog from "./IntroDialog";
import {useDeliveryDates} from "./useDeliveryDates";
import {getNumberOfMeals, getPortionPriceFromVariant, getProductVariant} from "../accountV2/common";
import {BottomBar} from "../v2/recipe/WeekRecipes";
import {Col, Container, Row} from "react-bootstrap";
import ButtonWithCounter from "../v2/component/buttonWithCounter/ButtonWithCounter";
import CustomerSubscriptionInfoBar from "./CustomerSubscriptionInfoBar";
import LogoIcon from "../icons/Logo";
import CheckoutNavigationItem from "./CheckoutNavigationItem";
import {useFullScreenModal} from "../v2/component/useFullScreenModal";
import CartOverlay from "../v2/cart/CartOverlay";
import {formatMoneyAmount} from "../accountV2/commonFormat";
import {usePricingTable} from "../v2/recipe/usePricingTable";
import {useTags} from "../filter/useTags";
import {Trans, useTranslation} from "react-i18next";
/* eslint-disable  react-hooks/exhaustive-deps */

const MealBoxesSelection = (): React.ReactElement => {
  const {t} = useTranslation();
  const cart = useMealkitCart();
  const order = cart.asOrder();
  const {values: availableRecipesIncludingAddons, addons: availableAddons, isLoading: isLoadingRecipes} =
    useWeekRecipes(WeekDate(cart.deliveryDate), cart.products.map(x => x.productId));

  const availableRecipes = availableRecipesIncludingAddons ? availableRecipesIncludingAddons.filter((recipe) => !availableAddons?.some(addon => addon.id === recipe.id)) : [];

  const {recipeTags, getTagFromString} = useTags(availableRecipes);
  const {selectedTagHandles} = useSelectedTagHandles();

  const selectedTags = useMemo(() => selectedTagHandles.map(it => getTagFromString(it)), [selectedTagHandles, getTagFromString]);

  const {load: loadDeliveryDates, data: deliveryDates} = useDeliveryDates();
  const [productIdToAdd, setProductIdToAdd] = useState<string | undefined>();

  useEffect(() => {
    if (!deliveryDates || (deliveryDates && deliveryDates.length === 0) || cart.deliveryDate === undefined) {
      return;
    }

    if (!cart.deliveryDate || !deliveryDates.includes(cart.deliveryDate)) {
      cart.resetWithDefaultDeliveryDate();
      return;
    }

    const allItemsAreValid = cart.checkAllItemsInDeliveryDate();
    if (!allItemsAreValid) {
      cart.resetWithDefaultDeliveryDate();
    }
  }, [availableRecipes, deliveryDates, cart.deliveryDate]);

  useEffect(() => {
    [
      document.querySelector(".tt-desktop-header"),
      document.querySelector(".tt-mobile-header"),
    ].forEach((it: HTMLElement) => {
      if (it?.style) {
        it.style.display = "none";
      }
    });

    (async () => {
      if (cart.isUninitialized) {
        cart.create(2, (await loadDeliveryDates())[0]);
      }

      const url = new URL(window.location.href);

      if (cart.status === "idle") {
        if (!cart.numberOfPeopleExplicit && !url.searchParams.get("people")) {
          cart.saveNumberOfPeople(2);
          openIntroDialog();
        } else {
          const people = Number(url.searchParams.get("people"));
          if (people === 2 || people === 4) cart.saveNumberOfPeople(people);
          url.searchParams.delete("people");
          history.replaceState(null, null, url.toString());
        }
      }

      if (url.searchParams.get("productId")) {
        const productId = Number(url.searchParams.get("productId"));
        setProductIdToAdd(`gid://shopify/ProductVariant/${productId}`);
      }
    })();
  }, [cart.isUninitialized, cart.status]);

  useEffect(() => {
    (async () => {
      if (cart.isReady && productIdToAdd) {
        if (!cart.hasProduct(productIdToAdd)) {
          cart.addProduct(productIdToAdd, await cart.getSellingPlanId());
          setProductIdToAdd(null);
        }
      }
    })();
  }, [cart.isReady, productIdToAdd]);

  const addRecipe = async (recipeId: string) => await cart.addProduct(recipeId, await cart.getSellingPlanId());
  const removeRecipe = async (recipeId: string) => await cart.removeProduct(recipeId);
  const addAddon = async (addonId: string) => await cart.addProduct(addonId);
  const removeAddon = async (addonId: string) => await cart.removeProduct(addonId);

  const onClickIsDisabled = useMemo(() => {
    const allMealsInOrder = order.products;
    const recipesInOrder = order.products?.filter(it => !availableAddons?.some(addon => addon.id === it.productId));

    return getNumberOfMeals(recipesInOrder) < MIN_RECIPES_FOR_SUBSCRIPTION ||
      getNumberOfMeals(allMealsInOrder) > MAX_RECIPES_FOR_SUBSCRIPTION;
  }, [order.products, availableAddons]);

  const {
    isOpen: cartOverlayIsOpen,
    openModal: openCartOverlay,
    closeModal: hideCartOverlay,
  } = useFullScreenModal("cart");

  const {
    isOpen: introDialogIsOpen,
    openModal: openIntroDialog,
    closeModal: closeIntroDialog,
  } = useFullScreenModal("introDialog");

  const {basePriceRecipe} = usePricingTable(availableRecipes);

  const currentPriceForCheapestProduct = basePriceRecipe ? getPortionPriceFromVariant(getProductVariant(cart.numberOfPeople, Math.max(cart.numberOfSubscribedRecipes, MIN_RECIPES_FOR_SUBSCRIPTION), basePriceRecipe), cart.numberOfPeople) : 0;

  const nextNumberOfSubscriptionMeals = cart.numberOfSubscribedRecipes + 1;
  const nextPriceForCheapestVariant = basePriceRecipe ? getPortionPriceFromVariant(getProductVariant(cart.numberOfPeople, Math.max(nextNumberOfSubscriptionMeals, MIN_RECIPES_FOR_SUBSCRIPTION + 1), basePriceRecipe), cart.numberOfPeople) : 0;

  const getLabel = () => {
    if (cart.numberOfSubscribedRecipes < MIN_RECIPES_FOR_SUBSCRIPTION) {
      return <Trans i18nKey="recipes.selection.minimum_to_save" values={{
        count: MIN_RECIPES_FOR_SUBSCRIPTION,
        currentPriceForCheapestVariant: formatMoneyAmount(currentPriceForCheapestProduct),
        nextPriceForCheapestVariant: formatMoneyAmount(nextPriceForCheapestVariant),
      }} components={{del: <del style={{color: "#ff000080"}} />}} />;
    }

    if (cart.numberOfSubscribedRecipes === MAX_RECIPES_FOR_SUBSCRIPTION || currentPriceForCheapestProduct === nextPriceForCheapestVariant) {
      return t("recipes.selection.save");
    }

    return <Trans i18nKey="recipes.selection.message" values={{
      numberOfMeals: nextNumberOfSubscriptionMeals,
      currentPriceForCheapestVariant: formatMoneyAmount(currentPriceForCheapestProduct),
      nextPriceForCheapestVariant: formatMoneyAmount(nextPriceForCheapestVariant),
    }} components={{del: <del style={{color: "#ff000080"}} />}} />;
  };

  if (isLoadingRecipes) {
    return <div className="d-flex justify-content-center">
      <Preloader />
    </div>;
  }

  return <>
    <div style={{overflow: "hidden"}}>
      <IntroDialog
        isOpen={introDialogIsOpen}
        closeModal={closeIntroDialog}
        numberOfPerson={cart.numberOfPeople || 2}
        setNumberOfPerson={(people) => cart.saveNumberOfPeople(people)}
      />
      <CartOverlay order={{...order, totalDiscount: cart.totalDiscountAmount}}
                   isOpen={cartOverlayIsOpen}
                   hide={hideCartOverlay}
                   addRecipe={addRecipe}
                   removeRecipe={removeRecipe}
                   addAddon={addAddon}
                   removeAddon={removeAddon}
                   onRecipeAdd={hideCartOverlay}
                   onAddonAdd={cart.proceedToAddons}
                   onContinue={cart.proceedToAddons}
                   withNumberOfPeopleSelection
                   setNumberOfPeople={cart.saveNumberOfPeople}
                   isCheckoutFlow={true}
      />
    </div>
    <div className="sticky-top shadow-sm" style={{backgroundColor: "#FFF", overflow: "hidden auto"}}>
      <Container>
        <Row className="pt-2 align-items-center">
          <Col xs={"auto"}>
            <LogoIcon width={isMobile() ? 30 : 40} height={isMobile() ? 30 : 40}
                      onClick={() => window.location.replace("https://www.cleankitchen.ee")}
            />
          </Col>
          <Col className="d-flex justify-content-evenly align-items-center px-0">
            <CheckoutNavigationItem index={1} isActive>
              {t("order.first_order_flow.recipes_step")}
            </CheckoutNavigationItem>
            <CheckoutNavigationItem index={2} onClick={onClickIsDisabled ? undefined : cart.proceedToAddons}>
              {t("order.first_order_flow.addons_step")}
            </CheckoutNavigationItem>
            <CheckoutNavigationItem index={3}>
              {t("order.first_order_flow.delivery_and_payment_step")}
            </CheckoutNavigationItem>
          </Col>
          <Col xs={"auto"} className="d-flex justify-content-end fs-4">
            <ButtonWithCounter label={"🛒"} counterValue={cart.numberOfMeals} onClick={openCartOverlay} />
          </Col>
        </Row>
      </Container>
      <Container>
        <ResponsiveRecipeFilter recipeCount={availableRecipes.length} recipeTags={recipeTags} />
      </Container>
      <Container>
        <CustomerSubscriptionInfoBar />
      </Container>
      <Container className="upcoming-orders my-2 fs-6">
        {getLabel()}
      </Container>
    </div>
    <div className="pt-2" style={{backgroundColor: "#FFF"}}>
      <Container>
        <Row>
          {availableRecipes?.map((recipe) => {
            const hasAllTagHandles = selectedTags
              ? selectedTags.every((tag) => recipe.tags.includes(tag?.handle) || recipe.tags.includes(tag?.name))
              : true;
            if (hasAllTagHandles) {
              const variant = getProductVariant(cart.numberOfPeople, cart.numberOfSubscribedRecipes, recipe);
              const price = Number(variant.price.amount);
              const pricePerPortion = price / cart.numberOfPeople;
              return <RecipeCard
                key={recipe.id}
                recipe={recipe}
                addRecipe={addRecipe}
                removeRecipe={removeRecipe}
                order={cart.asOrder()}
                surcharge={pricePerPortion}
                displayAsSurchargePerPortion={true}
                displaySurchargeAsDifference={false}
              />;
            }
          })}
        </Row>
      </Container>
    </div>
    {
      cart.isUninitialized && cart.state.cost?.totalAmount ? <></> :
        <BottomBar grossOrderTotal={Number(cart.state.cost?.subtotalAmount?.amount)}
                   discountCodes={cart.discountCodes}
                   totalDiscountAmount={cart.totalDiscountAmount} addDiscount={cart.addDiscount}
                   removeDiscount={cart.removeDiscount}
                   continueIsDisabled={onClickIsDisabled} onClick={cart.proceedToAddons}
                   continueButtonText={t("recipes.actions.continue")} />
    }
  </>;
};

export default MealBoxesSelection;
